import React, { useState, useEffect, useContext } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { fetchUserDetails, updateUser, fetchServices } from '../services/apiService';
import menuItemsByService from '../categories/serviceMenus';
import { useSidebar } from '../context/SidebarContext';

const UserEditForm = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { authTokens, impersonatedUserId } = useContext(AuthContext);
  const { isSidebarOpen } = useSidebar();
  const [userServices, setUserServices] = useState([]);
  const [userData, setUserData] = useState({
    email: '',
    username: '',
    password: '',
    service_ids: [],
    telefono: '',
    permiso_cre: '',
  });
  const [userRole, setUserRole] = useState(null);
  const [services, setServices] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadUserDataAndServices = async () => {
      try {
        const userDetails = await fetchUserDetails(userId, authTokens);
        const allServices = await fetchServices(authTokens);
        setUserRole(userDetails.role ?? null);
        setUserData({
          email: userDetails.email,
          username: userDetails.username,
          service_ids: userDetails.services.map((service) => service.id.toString()),
          telefono: userDetails.telefono ?? userDetails.phone ?? '',
          permiso_cre:
            userDetails.permiso_cre != null && userDetails.permiso_cre !== ''
              ? String(userDetails.permiso_cre)
              : '',
        });
        setServices(allServices);
      } catch (error) {
        console.error('Error al cargar los detalles del usuario o los servicios:', error);
        setError('Error al cargar los detalles del usuario o los servicios.');
      }
    };

    loadUserDataAndServices();
  }, [userId, authTokens]);

  useEffect(() => {
    const loadUserServices = async () => {
      if (authTokens && (impersonatedUserId || authTokens.userRole !== 'admin')) {
        try {
          const userIdToFetch = impersonatedUserId || authTokens.userId;
          const userDetails = await fetchUserDetails(userIdToFetch, authTokens);
          const serviceOrder = {
            'Anexo 30 y 31 2022': 1,
            'Anexo 30 y 31 2023': 2,
            'Anexo 30 y 31 2024': 3,
            'Anexo 30 y 31 2025': 4,
            'Dictamen de Diseño': 5,
            'NOM-016-CRE-2016': 6,
            'Anexo 30 y 31 para transporte o distribución': 7,
          };
          const sortedServices = userDetails.services.sort(
            (a, b) => (serviceOrder[a.name] || 0) - (serviceOrder[b.name] || 0),
          );
          setUserServices(sortedServices);
        } catch (error) {
          console.error('Error al cargar los servicios del usuario:', error);
        }
      }
    };

    loadUserServices();
  }, [authTokens, impersonatedUserId]);

  const handleServiceChange = (serviceId) => {
    const newServiceIds = userData.service_ids.includes(serviceId)
      ? userData.service_ids.filter((id) => id !== serviceId)
      : [...userData.service_ids, serviceId];
    setUserData({ ...userData, service_ids: newServiceIds });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserData({ ...userData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const updatedUserData = {
      email: userData.email,
      username: userData.username,
      service_ids: userData.service_ids.map(Number),
    };
    if (userData.password) {
      updatedUserData.password = userData.password;
    }
    if (userRole === 'admin') {
      updatedUserData.telefono = userData.telefono;
    }
    if (userRole === 'user') {
      updatedUserData.permiso_cre = userData.permiso_cre;
    }

    try {
      await updateUser(userId, updatedUserData, authTokens);
      console.log('Usuario actualizado con éxito');
      navigate('/superadminmanagement');
    } catch (error) {
      console.error('Error al actualizar el usuario:', error);
      setError('Error al actualizar el usuario. Por favor, verifica tus datos.');
    }
  };

  if (!authTokens) {
    return <div>Cargando...</div>;
  }

  return (
    <div className="flex min-h-screen bg-gray-100">
      <div
        className={`fixed md:fixed top-16 left-0 h-[calc(100vh-4rem)] overflow-y-auto transform
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          transition duration-200 ease-in-out
          w-64 bg-white shadow-lg z-40`}
      >
        <div className="p-8 border-b">
          <img src="/images/logo2.png" alt="Logo Petrotec" className="h-32 w-auto mx-auto" />
        </div>
        <nav className="mt-6">
          <Link
            to="/dashboard"
            className={`
              block px-4 py-3 hover:bg-blue-50 transition-colors duration-200
              text-gray-700 hover:text-blue-600 border-b border-gray-100
            `}
          >
            Inicio
          </Link>
          <div className="px-4 py-2">
            <h2 className="text-xs uppercase font-semibold text-gray-600 tracking-wide">
              Servicios Disponibles
            </h2>
          </div>
          {userServices.map((service) => {
            const firstMenuItem = menuItemsByService[service.name]?.[0];
            return (
              <Link
                key={service.id}
                to={firstMenuItem?.path || '#'}
                className="block px-4 py-3 hover:bg-blue-50 transition-colors duration-200 text-gray-700 hover:text-blue-600"
              >
                {service.name}
              </Link>
            );
          })}
        </nav>
      </div>

      <div className={`flex-1 transition-all duration-200 ${isSidebarOpen ? 'md:ml-64' : ''}`}>

        <div className="p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold my-8">Editar Usuario</h1>
          </div>
          <div className="bg-white shadow-lg rounded-lg p-8 max-w-3xl mx-auto">
            {error && <div className="text-red-500">{error}</div>}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email:</label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  value={userData.email}
                  onChange={handleChange}
                  required
                  className={`
                mt-1 block w-full border border-gray-300
                rounded-md shadow-sm py-2 px-3 focus:outline-none
                focus:ring-blue-500 focus:border-blue-500
              `}
                />
              </div>

              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700">Nombre de Usuario:</label>
                <input
                  id="username"
                  type="text"
                  name="username"
                  value={userData.username}
                  onChange={handleChange}
                  required
                  className={`
                mt-1 block w-full border border-gray-300
                rounded-md shadow-sm py-2 px-3 focus:outline-none
                focus:ring-blue-500 focus:border-blue-500
              `}
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">Contraseña:</label>
                <input
                  id="password"
                  type="password"
                  name="password"
                  value={userData.password}
                  onChange={handleChange}
                  placeholder="Dejar en blanco si no se desea cambiar la contraseña"
                  className={`
                mt-1 block w-full border border-gray-300
                rounded-md shadow-sm py-2 px-3 focus:outline-none
                focus:ring-blue-500 focus:border-blue-500
              `}
                />
              </div>

              {userRole === 'admin' && (
              <div>
                <label htmlFor="telefono" className="block text-sm font-medium text-gray-700">Teléfono:</label>
                <input
                  id="telefono"
                  type="tel"
                  name="telefono"
                  value={userData.telefono}
                  onChange={handleChange}
                  placeholder="Número de teléfono"
                  autoComplete="tel"
                  className={`
                  mt-1 block w-full border border-gray-300
                  rounded-md shadow-sm py-2 px-3 focus:outline-none
                  focus:ring-blue-500 focus:border-blue-500
                `}
                />
              </div>
              )}

              {userRole === 'user' && (
              <div>
                <label htmlFor="permiso_cre" className="block text-sm font-medium text-gray-700">
                  Permiso CRE
                </label>
                <input
                  id="permiso_cre"
                  type="text"
                  name="permiso_cre"
                  value={userData.permiso_cre}
                  onChange={handleChange}
                  placeholder="CRE-123"
                  autoComplete="off"
                  className={`
                  mt-1 block w-full border border-gray-300
                  rounded-md shadow-sm py-2 px-3 focus:outline-none
                  focus:ring-blue-500 focus:border-blue-500
                `}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Identificador del permiso CRE (ej. CRE-123).
                </p>
              </div>
              )}

              <div>
                <label htmlFor="services" className="block text-sm font-medium text-gray-700">Servicios Activos:</label>
                <div className="mt-1">
                  {services.map((service) => (
                    <div key={service.id} className="flex items-center mb-2">
                      <input
                        id={`service-${service.id}`}
                        type="checkbox"
                        name="service_ids"
                        value={service.id}
                        checked={userData.service_ids.includes(service.id.toString())}
                        onChange={() => handleServiceChange(service.id.toString())}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor={`service-${service.id}`} className="ml-2 block text-sm text-gray-900">
                        {service.name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <button type="submit" className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded">
                Actualizar
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserEditForm;
